import { create } from "zustand";
import { useMemo } from "react";
import {
  BASE_FORMATS,
  BASE_SECTIONS,
  type DocFormat,
  type DocSection,
} from "@/content/designDocs";

const STORAGE_KEY = "hippo.designDocContent";

interface Persisted {
  addedFormats: DocFormat[];
  hiddenFormatIds: string[];
  formatOverrides: Record<string, DocFormat>;
  addedSections: DocSection[];
  hiddenSectionIds: string[];
  sectionOverrides: Record<string, DocSection>;
}

const EMPTY: Persisted = {
  addedFormats: [],
  hiddenFormatIds: [],
  formatOverrides: {},
  addedSections: [],
  hiddenSectionIds: [],
  sectionOverrides: {},
};

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      return { ...EMPTY, ...p };
    }
  } catch {
    /* ignore */
  }
  return { ...EMPTY };
}

/** Base seeds (minus hidden, with overrides applied) followed by user additions. */
function overlay<T extends { id: string }>(
  base: T[],
  added: T[],
  hidden: string[],
  overrides: Record<string, T>
): T[] {
  const hide = new Set(hidden);
  return [...base.filter((c) => !hide.has(c.id)).map((c) => overrides[c.id] ?? c), ...added];
}

function baseHasId<T extends { id: string }>(base: T[], id: string): boolean {
  return base.some((c) => c.id === id);
}

export function slugDocId(prefix: string, name: string): string {
  const base = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "item";
  return `${prefix}-${base}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

interface State extends Persisted {
  effectiveFormats: () => DocFormat[];
  effectiveSections: () => DocSection[];
  sectionMap: () => Record<string, DocSection>;

  isHiddenFormat: (id: string) => boolean;
  isCustomFormat: (id: string) => boolean;
  isEditedFormat: (id: string) => boolean;
  saveFormat: (f: DocFormat) => void;
  removeFormat: (id: string) => void;
  revertFormat: (id: string) => void;

  isHiddenSection: (id: string) => boolean;
  isCustomSection: (id: string) => boolean;
  isEditedSection: (id: string) => boolean;
  saveSection: (s: DocSection) => void;
  removeSection: (id: string) => void;
  revertSection: (id: string) => void;
}

export const useDesignDocContentStore = create<State>((set, get) => {
  const persist = () => {
    const s = get();
    const data: Persisted = {
      addedFormats: s.addedFormats,
      hiddenFormatIds: s.hiddenFormatIds,
      formatOverrides: s.formatOverrides,
      addedSections: s.addedSections,
      hiddenSectionIds: s.hiddenSectionIds,
      sectionOverrides: s.sectionOverrides,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  return {
    ...load(),

    effectiveFormats: () =>
      overlay(BASE_FORMATS, get().addedFormats, get().hiddenFormatIds, get().formatOverrides),
    effectiveSections: () =>
      overlay(BASE_SECTIONS, get().addedSections, get().hiddenSectionIds, get().sectionOverrides),
    sectionMap: () => Object.fromEntries(get().effectiveSections().map((s) => [s.id, s])),

    // ---- Formats ----
    isHiddenFormat: (id) => get().hiddenFormatIds.includes(id),
    isCustomFormat: (id) => get().addedFormats.some((f) => f.id === id),
    isEditedFormat: (id) => Boolean(get().formatOverrides[id]),
    saveFormat: (f) => {
      if (baseHasId(BASE_FORMATS, f.id)) {
        set({ formatOverrides: { ...get().formatOverrides, [f.id]: f } });
      } else if (get().addedFormats.some((x) => x.id === f.id)) {
        set({ addedFormats: get().addedFormats.map((x) => (x.id === f.id ? f : x)) });
      } else {
        set({ addedFormats: [...get().addedFormats, f] });
      }
      persist();
    },
    removeFormat: (id) => {
      if (baseHasId(BASE_FORMATS, id)) {
        if (!get().hiddenFormatIds.includes(id)) set({ hiddenFormatIds: [...get().hiddenFormatIds, id] });
      } else {
        const overrides = { ...get().formatOverrides };
        delete overrides[id];
        set({ addedFormats: get().addedFormats.filter((f) => f.id !== id), formatOverrides: overrides });
      }
      persist();
    },
    revertFormat: (id) => {
      const overrides = { ...get().formatOverrides };
      delete overrides[id];
      set({ formatOverrides: overrides, hiddenFormatIds: get().hiddenFormatIds.filter((x) => x !== id) });
      persist();
    },

    // ---- Sections ----
    isHiddenSection: (id) => get().hiddenSectionIds.includes(id),
    isCustomSection: (id) => get().addedSections.some((s) => s.id === id),
    isEditedSection: (id) => Boolean(get().sectionOverrides[id]),
    saveSection: (sec) => {
      if (baseHasId(BASE_SECTIONS, sec.id)) {
        set({ sectionOverrides: { ...get().sectionOverrides, [sec.id]: sec } });
      } else if (get().addedSections.some((x) => x.id === sec.id)) {
        set({ addedSections: get().addedSections.map((x) => (x.id === sec.id ? sec : x)) });
      } else {
        set({ addedSections: [...get().addedSections, sec] });
      }
      persist();
    },
    removeSection: (id) => {
      if (baseHasId(BASE_SECTIONS, id)) {
        if (!get().hiddenSectionIds.includes(id)) set({ hiddenSectionIds: [...get().hiddenSectionIds, id] });
      } else {
        const overrides = { ...get().sectionOverrides };
        delete overrides[id];
        set({ addedSections: get().addedSections.filter((s) => s.id !== id), sectionOverrides: overrides });
      }
      persist();
    },
    revertSection: (id) => {
      const overrides = { ...get().sectionOverrides };
      delete overrides[id];
      set({ sectionOverrides: overrides, hiddenSectionIds: get().hiddenSectionIds.filter((x) => x !== id) });
      persist();
    },
  };
});

// ---- Hooks ----

export function useDocFormats(): DocFormat[] {
  const added = useDesignDocContentStore((s) => s.addedFormats);
  const hidden = useDesignDocContentStore((s) => s.hiddenFormatIds);
  const overrides = useDesignDocContentStore((s) => s.formatOverrides);
  return useMemo(() => overlay(BASE_FORMATS, added, hidden, overrides), [added, hidden, overrides]);
}

export function useDocSections(): DocSection[] {
  const added = useDesignDocContentStore((s) => s.addedSections);
  const hidden = useDesignDocContentStore((s) => s.hiddenSectionIds);
  const overrides = useDesignDocContentStore((s) => s.sectionOverrides);
  return useMemo(() => overlay(BASE_SECTIONS, added, hidden, overrides), [added, hidden, overrides]);
}

export function useDocSectionMap(): Record<string, DocSection> {
  const sections = useDocSections();
  return useMemo(() => Object.fromEntries(sections.map((s) => [s.id, s])), [sections]);
}
